import { lazy, useState } from 'react';
import { useIntl } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';

import ContentManager from 'src/componentes/content-manager';
import ViewDocument from 'src/componentes/arquivo-upload/ViewDocument';
import { DownloadFileBucket as downloadFileBucket } from 'src/services/downloadFileUrl';
import {
  getSharepointFileEditUrl,
  getSharepointFileEmbedUrl,
} from 'src/components/integracoes/microsoft/types';
import {
  Calendar,
  Database,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Trash2,
  User,
  FileText,
} from 'lucide-react';
import { TIPO_COPIA_DOCUMENTO } from './copiaControladaDocumentoConstants';

import './DocumentoArquivosList.css';
import GerarCopiaControladaModal from './GerarCopiaControladaModal';
import { STATUS_DOCUMENTO } from 'src/utils/constants';

const SharepointDocumentDialog = lazy(() =>
  import('src/components/integracoes/microsoft/sharepoint/document-dialog')
);

const formatarData = (data, intl) => {
  if (!data) return '-';

  const parsedDate = new Date(data);
  if (Number.isNaN(parsedDate.getTime())) return '-';

  return intl.formatDate(parsedDate, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatarTamanhoArquivo = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string' && /[a-zA-Z]/.test(value)) return value;

  const tamanho = Number(value);
  if (!Number.isFinite(tamanho) || tamanho <= 0) return '-';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    Math.floor(Math.log(tamanho) / Math.log(1024)),
    units.length - 1
  );
  const size = tamanho / 1024 ** exponent;

  return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${
    units[exponent]
  }`;
};

const ehPdfOuDoc = (fileName) => {
  if (typeof fileName !== 'string') return false;
  const lower = fileName.toLowerCase();
  return (
    lower.endsWith('.pdf') || lower.endsWith('.docx') || lower.endsWith('.doc')
  );
};

const isPdfFile = (fileName, convertWordToPdf) => {
  return (
    typeof fileName === 'string' &&
    (fileName.toLowerCase().endsWith('.pdf') ||
      (convertWordToPdf &&
        (fileName.toLowerCase().endsWith('.docx') ||
          fileName.toLowerCase().endsWith('.doc'))))
  );
};

const obterNomeArquivo = (arquivo) => arquivo?.nome;

const obterNomeExibicaoArquivo = (arquivo) =>
  arquivo?.nomeOriginal || obterNomeArquivo(arquivo) || '-';

const obterNomeDownloadArquivo = (arquivo) =>
  arquivo?.nomeOriginal || obterNomeArquivo(arquivo);

function DocumentoArquivosList({
  arquivos,
  className = '',
  compact = false,
  errors = false,
  showUploadDate = false,
  disabledDownload = false,
  disabledDelete = false,
  allowEditSharepoint = false,
  onDelete,
  convertWordToPdf = true,
  urlCustom = 'DocumentoDownload/DownloadFile',
  documentName,
  documentType,
  tipoCopiaDocumento,
  quantidadePermitidaCopias,
  statusAtual,
  parameters = {},
}) {
  const intl = useIntl();
  const [copiasGeradasLocalmente, setCopiasGeradasLocalmente] = useState({});
  const listaArquivos = Array.isArray(arquivos) ? arquivos : [];
  const hasDeleteAction = typeof onDelete === 'function';
  const isCopiaControlada =
    tipoCopiaDocumento === TIPO_COPIA_DOCUMENTO.COPIA_CONTROLADA;
  const isCopiaNaoControlada =
    tipoCopiaDocumento === TIPO_COPIA_DOCUMENTO.COPIA_NAO_CONTROLADA;
  const isVigente = statusAtual?.id === STATUS_DOCUMENTO.VIGENTE;

  const ehArquivoSharepoint = (arquivo) => !!arquivo?.ehArquivoSharepoint;

  const podeVisualizarArquivo = (arquivo) => {
    if (ehArquivoSharepoint(arquivo))
      return !!getSharepointFileEmbedUrl(
        arquivo?.webUrl,
        arquivo?.arquivoId,
        arquivo?.extensao
      );

    return isPdfFile(obterNomeArquivo(arquivo), convertWordToPdf);
  };

  const podeBaixarArquivo = (arquivo) =>
    !ehArquivoSharepoint(arquivo) &&
    !!obterNomeArquivo(arquivo) &&
    !disabledDownload;

  const podeAbrirPeloNomeArquivo = (arquivo) =>
    !!obterNomeArquivo(arquivo) &&
    (podeVisualizarArquivo(arquivo) || podeBaixarArquivo(arquivo));

  const possuiLimiteDisponivelCopias = (arquivo) => {
    const realizadas =
      (arquivo.quantidadeCopiasRealizadas || 0) +
      (copiasGeradasLocalmente[arquivo?.id] || 0);
    return realizadas < quantidadePermitidaCopias;
  };

  const handleVisualizarArquivo = (arquivo) => {
    const arquivoNome = obterNomeArquivo(arquivo);
    const podeVisualizar = podeVisualizarArquivo(arquivo);
    if (!arquivoNome || !podeVisualizar) return;

    if (ehArquivoSharepoint(arquivo)) {
      ContentManager.addContent(<SharepointDocumentDialog arquivo={arquivo} />);
      return;
    }

    ContentManager.addContent(
      <ViewDocument
        fileName={arquivoNome}
        onlyRead={
          isCopiaControlada ||
          (!isCopiaNaoControlada && podeVisualizar && disabledDownload)
        }
        convertWordToPdf={convertWordToPdf}
        urlCustom={urlCustom}
        documentName={documentName}
        documentType={documentType}
        parameters={{
          ...parameters,
          isDownload: false,
          ehVisualizacao: isCopiaControlada,
        }}
      />
    );
  };

  const handleDownloadArquivo = (arquivo) => {
    const arquivoNome = obterNomeArquivo(arquivo);
    if (!arquivoNome || disabledDownload) return;

    downloadFileBucket(
      arquivoNome,
      convertWordToPdf,
      urlCustom,
      {
        ...parameters,
        isDownload: true,
      },
      obterNomeDownloadArquivo(arquivo)
    );
  };

  const handleDownloadCopiaNaoControlada = (arquivo) => {
    const arquivoNome = obterNomeArquivo(arquivo);
    if (!arquivoNome) return;

    downloadFileBucket(arquivoNome, convertWordToPdf, urlCustom, {
      ...parameters,
      isDownload: true,
    });
  };

  const handleAbrirNoSharepoint = (arquivo) => {
    const url = allowEditSharepoint
      ? getSharepointFileEditUrl(
          arquivo?.webUrl,
          arquivo?.arquivoId,
          arquivo?.extensao
        )
      : getSharepointFileEmbedUrl(
          arquivo?.webUrl,
          arquivo?.arquivoId,
          arquivo?.extensao
        );
    if (url) window.open(url, '_blank', 'noopener');
  };

  const handleAbrirPeloNomeArquivo = (arquivo) => {
    if (!podeAbrirPeloNomeArquivo(arquivo)) return;

    if (ehArquivoSharepoint(arquivo) && allowEditSharepoint) {
      handleAbrirNoSharepoint(arquivo);
      return;
    }

    if (podeVisualizarArquivo(arquivo)) {
      handleVisualizarArquivo(arquivo);
      return;
    }

    if (podeBaixarArquivo(arquivo)) {
      handleDownloadArquivo(arquivo);
    }
  };

  const handleCopiaControladaClick = (arquivo) => {
    ContentManager.addContent(
      <GerarCopiaControladaModal
        quantidadePermitidaCopias={quantidadePermitidaCopias}
        arquivo={arquivo}
        urlCustom={urlCustom}
        convertWordToPdf={convertWordToPdf}
        parameters={parameters}
        onSuccess={(quantidade) =>
          setCopiasGeradasLocalmente((prev) => ({
            ...prev,
            [arquivo?.id]: (prev[arquivo?.id] || 0) + quantidade,
          }))
        }
      />
    );
  };

  if (listaArquivos.length === 0) return null;

  return (
    <div
      className={`documento-arquivos-list${
        compact ? ' documento-arquivos-list--compact' : ''
      }${errors ? ' documento-arquivos-list--error' : ''}${
        className ? ` ${className}` : ''
      }`}
    >
      {listaArquivos.map((arquivo, idx) => {
        const isSharepoint = ehArquivoSharepoint(arquivo);
        const podeVisualizar = podeVisualizarArquivo(arquivo);
        const podeBaixar = podeBaixarArquivo(arquivo);
        const podeAbrirPeloNome = podeAbrirPeloNomeArquivo(arquivo);
        const podeCopiar = !isSharepoint && ehPdfOuDoc(obterNomeArquivo(arquivo));
        const tamanhoArquivo = formatarTamanhoArquivo(arquivo?.tamanho);
        const possuiLimiteCopias = possuiLimiteDisponivelCopias(arquivo);
        const hintVisualizacao = podeVisualizar
          ? intl.formatMessage({ id: 'visualizarDocumento' })
          : intl.formatMessage({
              id: 'hintFormatoNaoDisponivelParaVisualizacao',
            });

        return (
          <div
            key={arquivo?.id || arquivo?.guid || arquivo?.nome || idx}
            className={`documento-arquivos-list__file-row${
              idx > 0 ? ' documento-arquivos-list__file-row--with-divider' : ''
            }`}
          >
            <div className="documento-arquivos-list__file-main">
              <button
                type="button"
                onClick={() => handleAbrirPeloNomeArquivo(arquivo)}
                disabled={!podeAbrirPeloNome}
                className="documento-arquivos-list__file-name-button"
                title={obterNomeExibicaoArquivo(arquivo)}
              >
                {obterNomeExibicaoArquivo(arquivo)}
              </button>
              <div className="documento-arquivos-list__file-meta-row">
                {showUploadDate && (
                  <div className="documento-arquivos-list__file-meta-item">
                    <Calendar size={compact ? 12 : 16} />
                    <span className="documento-arquivos-list__file-meta-text">
                      {intl.formatMessage({ id: 'labelUploadEm' })}{' '}
                      {formatarData(arquivo?.dataInclusao, intl)}
                    </span>
                  </div>
                )}
                <div className="documento-arquivos-list__file-meta-item">
                  <User size={compact ? 12 : 16} />
                  <span className="documento-arquivos-list__file-meta-text">
                    {intl.formatMessage({ id: 'inseridoPor' })}{' '}
                    {arquivo?.responsavelNome || '-'}
                  </span>
                </div>
                <div className="documento-arquivos-list__file-meta-item">
                  <Database size={compact ? 12 : 16} />
                  <span className="documento-arquivos-list__file-meta-text">
                    {tamanhoArquivo}
                  </span>
                </div>
              </div>
            </div>

            <div className="documento-arquivos-list__actions">
              {hasDeleteAction && (
                <button
                  type="button"
                  disabled={disabledDelete}
                  onClick={() => onDelete(arquivo)}
                  className="documento-arquivos-list__action-button documento-arquivos-list__action-button--ghost"
                  title={intl.formatMessage({ id: 'label.excluir' })}
                >
                  <Trash2 size={compact ? 20 : 24} />
                </button>
              )}
              {isCopiaControlada && (
                <>
                  <span
                    id={`btn-download-copia-${arquivo?.id ?? idx}`}
                    style={{ display: 'inline-block' }}
                  >
                    <button
                      type="button"
                      disabled={
                        !podeCopiar || !isVigente || !possuiLimiteCopias
                      }
                      onClick={() => handleCopiaControladaClick(arquivo)}
                      className="documento-arquivos-list-copia-controlada__action-button documento-arquivos-list-copia-controlada__action-button--ghost"
                    >
                      <FileText
                        size={compact ? 14 : 18}
                        style={{ pointerEvents: 'none' }}
                      />
                      <span className="documento-arquivos-list-copia-controlada__label">
                        {intl.formatMessage({
                          id: 'labelGerarCopiaControlada',
                        })}
                      </span>
                    </button>
                  </span>
                  <UncontrolledTooltip
                    target={`btn-download-copia-${arquivo?.id ?? idx}`}
                    className="tooltip-copia"
                  >
                    {!podeCopiar
                      ? intl.formatMessage({
                          id: 'labelFormatoNaoPermitidoCopiaControlada',
                        })
                      : !isVigente
                      ? intl.formatMessage({
                          id: 'labelCopiaVigencia',
                        }) + ` ${statusAtual?.nome}`
                      : !possuiLimiteCopias
                      ? intl.formatMessage({
                          id: 'labelCopiaControladaLimiteAtingido',
                        })
                      : intl.formatMessage({
                          id: 'labelGerarCopiaControlada',
                        })}
                  </UncontrolledTooltip>
                </>
              )}

              {isCopiaNaoControlada && (
                <>
                  <span
                    id={`btn-download-copia-${arquivo?.id ?? idx}`}
                    style={{ display: 'inline-block' }}
                  >
                    <button
                      type="button"
                      disabled={!podeCopiar || !isVigente}
                      onClick={() => handleDownloadCopiaNaoControlada(arquivo)}
                      className="documento-arquivos-list-copia-controlada__action-button documento-arquivos-list-copia-controlada__action-button--ghost"
                    >
                      <FileText
                        size={compact ? 14 : 18}
                        style={{ pointerEvents: 'none' }}
                      />
                      <span className="documento-arquivos-list-copia-controlada__label">
                        {intl.formatMessage({
                          id: 'labelGerarCopiaNaoControlada',
                        })}
                      </span>
                    </button>
                  </span>
                  <UncontrolledTooltip
                    target={`btn-download-copia-${arquivo?.id ?? idx}`}
                    popperClassName="tooltip-copia"
                  >
                    {!podeCopiar
                      ? intl.formatMessage({
                          id: 'labelFormatoNaoPermitidoCopiaControlada',
                        })
                      : !isVigente
                      ? intl.formatMessage({
                          id: 'labelCopiaVigencia',
                        }) + ` ${statusAtual?.nome}`
                      : intl.formatMessage({
                          id: 'labelGerarCopiaNaoControlada',
                        })}
                  </UncontrolledTooltip>
                </>
              )}

              {isSharepoint && (
                <button
                  type="button"
                  onClick={() => handleAbrirNoSharepoint(arquivo)}
                  className="documento-arquivos-list__action-button documento-arquivos-list__action-button--ghost"
                  title={intl.formatMessage({
                    id: allowEditSharepoint
                      ? 'labelEditarDocumentoOnline'
                      : 'labelAbrirNoSharepoint',
                  })}
                >
                  <ExternalLink size={compact ? 20 : 24} />
                </button>
              )}
              {!isSharepoint && !isCopiaControlada && !isCopiaNaoControlada && (
                <button
                  type="button"
                  disabled={!podeBaixar}
                  onClick={() => handleDownloadArquivo(arquivo)}
                  className="documento-arquivos-list__action-button documento-arquivos-list__action-button--ghost"
                  title={intl.formatMessage({ id: 'label.download' })}
                >
                  <Download size={compact ? 20 : 24} />
                </button>
              )}
              <button
                type="button"
                disabled={!podeVisualizar}
                onClick={() => handleVisualizarArquivo(arquivo)}
                className="documento-arquivos-list__action-button documento-arquivos-list__action-button--primary"
                title={hintVisualizacao}
              >
                {podeVisualizar ? (
                  <Eye size={compact ? 20 : 24} />
                ) : (
                  <EyeOff size={compact ? 20 : 24} />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default DocumentoArquivosList;
